import browser from "webextension-polyfill";
import * as ewe from "@eyeo/webext-ad-filtering-solution";
import { ytAllowlistStartDate } from "./detection.types.js";
import { youTubeAlreadyAllowListed, youTubeAutoAllowlisted, youTubeWallDetected, youTubeNavigation, ytRecordEvent, contextTamperingEvent, adShownEvent, errorEvent, AllowlistScope, ErrorType, } from "../shared/index.js";
const logger = new ewe.telemetry.ServerLogger("youtube");
const captureStartDate = function (parameters) {
    if (parameters.prefs.get(ytAllowlistStartDate) === 0) {
        parameters.prefs.set(ytAllowlistStartDate, Date.now());
        parameters.logger.debug("[yt-detection]: set start date", new Date(parameters.prefs.get(ytAllowlistStartDate)).toLocaleDateString());
    }
};
const captureDateOnUpdate = (parameters, details) => {
    if (details.reason === "update" || details.reason === "install") {
        captureStartDate(parameters);
    }
};
async function hasAllowlistedFilter(tabId) {
    let isAllowlisted = false;
    if (typeof tabId !== "undefined") {
        const filters = await ewe.filters.getAllowingFilters(tabId);
        isAllowlisted = filters.some((f) => f.includes("youtube.com"));
    }
    return isAllowlisted;
}
const processYouTubeWallMessage = async (message, sender) => {
    var _a, _b;
    const tabId = (_a = sender.tab) === null || _a === void 0 ? void 0 : _a.id;
    const tabURL = (_b = sender.tab) === null || _b === void 0 ? void 0 : _b.url;
    if (typeof tabId === "undefined" || typeof tabURL === "undefined") {
        return;
    }
    const senderURL = new URL(tabURL);
    const host = senderURL.hostname.replace(/^www\./, "");
    const allowlistUrl = host;
    const filters = await ewe.filters.getAllowingFilters(tabId);
    const isAllowListed = filters.some((f) => f.includes(allowlistUrl));
    const eventName = isAllowListed
        ? youTubeAlreadyAllowListed
        : youTubeWallDetected;
    logger.behavior(eventName, {
        adwallCategory: message.hasDismiss ? "soft" : "hard",
        userLoggedIn: message.userLoggedIn ? "1" : "0",
        isAllowListed: isAllowListed ? "1" : "0",
        allowlistScope: AllowlistScope.TAB,
    });
    if (isAllowListed) {
        return;
    }
    const ruleText = `@@||${allowlistUrl}^$document`;
    const metadata = {
        expiresByTabId: tabId,
        origin: "yt-auto",
        created: Date.now(),
    };
    await ewe.filters.add(ruleText, metadata);
    if (message.currentPlaybackTime > 5) {
        senderURL.searchParams.set("t", Math.floor(message.currentPlaybackTime).toString());
        await browser.tabs.update(tabId, { url: senderURL.href });
    }
    else {
        await browser.tabs.reload(tabId);
    }
    logger.behavior(youTubeAutoAllowlisted, {
        allowlistScope: AllowlistScope.TAB,
    });
};
export function start(parameters) {
    const captureDateOnUpdatedWithParameters = captureDateOnUpdate.bind(null, parameters);
    browser.runtime.onInstalled.addListener(captureDateOnUpdatedWithParameters);
    browser.webRequest.onCompleted.addListener(async (details) => {
        const { url, tabId } = details;
        const parsedUrl = new URL(url);
        const errorMsg = parsedUrl.searchParams.get("msg");
        const ytClientVersion = parsedUrl.searchParams.get("client.version");
        const stack = parsedUrl.searchParams.get("stack");
        const type = parsedUrl.searchParams.get("type");
        const [isAllowlisted, isPremium] = await Promise.all([
            hasAllowlistedFilter(tabId),
            ewe.account.hasPremium(),
        ]);
        if ((errorMsg === null || errorMsg === void 0 ? void 0 : errorMsg.includes("read-only")) &&
            (errorMsg === null || errorMsg === void 0 ? void 0 : errorMsg.includes("non-configurable"))) {
            logger.behavior(contextTamperingEvent, {
                applicationVersion: ytClientVersion !== null && ytClientVersion !== void 0 ? ytClientVersion : "",
                isAllowListed: isAllowlisted ? "1" : "0",
                isPremium,
            });
        }
        else {
            logger.behavior(errorEvent, {
                isAllowListed: isAllowlisted ? "1" : "0",
                isPremium,
                error: type !== null && type !== void 0 ? type : "",
                applicationVersion: ytClientVersion !== null && ytClientVersion !== void 0 ? ytClientVersion : "",
                category: ErrorType.UNCAUGHT_EXCEPTION,
                errorMsg: stack !== null && stack !== void 0 ? stack : "",
            });
        }
    }, { urls: ["*://*.youtube.com/error_204*"] });
    browser.webRequest.onCompleted.addListener(async (details) => {
        const { url, tabId } = details;
        const parsedUrl = new URL(url);
        const isFinalEvent = parsedUrl.searchParams.get("final");
        const totalAdTime = parsedUrl.searchParams.get("len");
        const adShownTime = parsedUrl.searchParams.get("rt");
        const adFormat = parsedUrl.searchParams.get("adformat");
        const contentVideo = parsedUrl.searchParams.get("content_v");
        const [isAllowlisted, isPremium] = await Promise.all([
            hasAllowlistedFilter(tabId),
            ewe.account.hasPremium(),
        ]);
        if (isFinalEvent !== "1" ||
            !totalAdTime ||
            !adShownTime ||
            !contentVideo) {
            return;
        }
        logger.behavior(adShownEvent, {
            isAllowListed: isAllowlisted ? "1" : "0",
            isPremium,
            adFormat: adFormat !== null && adFormat !== void 0 ? adFormat : "",
            totalAdDurationMs: Math.floor(Number(totalAdTime) * 1000),
            adShownDurationMs: Math.floor(Number(adShownTime) * 1000),
        });
    }, { urls: ["*://*.youtube.com/api/stats/watchtime?*el=adunit*"] });
    browser.webRequest.onCompleted.addListener(async (details) => {
        const { url, tabId } = details;
        const parsedUrl = new URL(url);
        const error = parsedUrl.searchParams.get("error");
        const clientVersion = parsedUrl.searchParams.get("cver");
        if (!error) {
            return;
        }
        const [isAllowlisted, isPremium] = await Promise.all([
            hasAllowlistedFilter(tabId),
            ewe.account.hasPremium(),
        ]);
        logger.behavior(errorEvent, {
            isAllowListed: isAllowlisted ? "1" : "0",
            isPremium,
            error,
            applicationVersion: clientVersion !== null && clientVersion !== void 0 ? clientVersion : "",
            category: ErrorType.SWW_ERROR,
            errorMsg: "",
        });
    }, { urls: ["*://*.youtube.com/api/stats/qoe?*"] });
    parameters.port.on(youTubeWallDetected, processYouTubeWallMessage);
    parameters.port.on(youTubeNavigation, async (message, sender) => {
        logger.behavior(youTubeNavigation, {
            userLoggedIn: message.userLoggedIn ? "1" : "0",
        });
    });
    parameters.port.on(ytRecordEvent, async ({ eventName, eventPayload }, sender) => {
        var _a;
        const tabId = (_a = sender.tab) === null || _a === void 0 ? void 0 : _a.id;
        const [isAllowlisted, isPremium] = await Promise.all([
            hasAllowlistedFilter(tabId),
            ewe.account.hasPremium(),
        ]);
        logger.behavior(eventName, {
            ...eventPayload,
            isAllowListed: isAllowlisted ? "1" : "0",
            isPremium,
        });
    });
    const trustedEventNames = [
        youTubeWallDetected,
        youTubeNavigation,
        ytRecordEvent,
    ];
    parameters.addTrustedMessageTypes("https://youtube.com", trustedEventNames);
    parameters.addTrustedMessageTypes("https://www.youtube.com", trustedEventNames);
    parameters.addTrustedMessageTypes("https://youtube-nocookie.com", trustedEventNames);
    parameters.addTrustedMessageTypes("https://www.youtube-nocookie.com", trustedEventNames);
}
